import { InfoOutlineIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Center,
  Flex,
  Spinner,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { Instant } from "@js-joda/core";
import { useQuery } from "@tanstack/react-query";
import { ResponseOf } from "../../../../core/api";
import useApi from "../../../../shared/hooks/useApi";
import { CaregiverId } from "../../../../shared/schema/schema";
import { dateFormatter } from "../../../../shared/utils/date-formatter";
import UnblockAlertDialog from "./UnblockAlertDialog";

type RnPerformanceBlock = ResponseOf<
  "get",
  "./caregivers/:caregiverId/rn_performance_blocks"
>["blocks"][number];

interface Props {
  caregiverId: CaregiverId;
}

export default function RnPerformanceBlocksTable(props: Props) {
  const { queries } = useApi();

  const query = useQuery(queries.caregiver.rnPerformanceBlocks(props.caregiverId));

  return (
    <Box mt={6}>
      <Flex align="center" gap={2} mb={4}>
        <Text fontSize="lg" fontWeight="semibold">
          Performance Blocks History
        </Text>
        <Tooltip
          hasArrow
          label="RN caregivers may be temporarily blocked from accepting visits due to high cancellation rates."
          placement="top"
        >
          <InfoOutlineIcon color="gray.500" cursor="help" />
        </Tooltip>
      </Flex>

      <TableContainer>
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Level</Th>
              <Th>Duration</Th>
              <Th>From</Th>
              <Th>Until</Th>
              <Th>Cancel %</Th>
              <Th>Late %</Th>
              <Th>Status</Th>
              <Th>Note</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {(() => {
              switch (query.status) {
                case "pending":
                  return (
                    <Tr>
                      <Td colSpan={9}>
                        <Center py={4}>
                          <Spinner size="sm" />
                        </Center>
                      </Td>
                    </Tr>
                  );
                case "error":
                  return (
                    <Tr>
                      <Td colSpan={9}>
                        <Center py={4}>
                          <Text color="red.500">Failed to load performance blocks</Text>
                        </Center>
                      </Td>
                    </Tr>
                  );
                case "success":
                  return query.data.length === 0 ? (
                    <Tr>
                      <Td colSpan={9}>
                        <Center bg="gray.50" color="gray.500" py={4} rounded="md">
                          No performance blocks on record.
                        </Center>
                      </Td>
                    </Tr>
                  ) : (
                    query.data.map((block) => (
                      <BlockRow key={block.id} block={block} onUnblock={query.refetch} />
                    ))
                  );
              }
            })()}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function BlockRow(props: { block: RnPerformanceBlock; onUnblock: () => void }) {
  const { block, onUnblock } = props;
  const disclosure = useDisclosure();

  const formatBlockLevel = (level: number) => {
    if (level === 16) {
      return "16 (Term)";
    }
    return `${level}`;
  };

  const formatDuration = (blockedAt: Instant, blockedUntil: Instant) => {
    const days = Math.round(
      (blockedUntil.toEpochMilli() - blockedAt.toEpochMilli()) / (1000 * 60 * 60 * 24)
    );
    return `${days} day${days !== 1 ? "s" : ""}`;
  };

  const formatPercentage = (value: number) => {
    return `${Math.round(value)}%`;
  };

  const renderStatus = () => {
    if (block.isActive) {
      return (
        <Tag colorScheme="red" size="sm">
          Active
        </Tag>
      );
    }

    switch (block.unblockedReason) {
      case "expired":
        return (
          <Tag colorScheme="gray" size="sm">
            Expired
          </Tag>
        );
      case "manual":
        return (
          <Tag colorScheme="blue" size="sm">
            Unblocked
          </Tag>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Tr>
        <Td>
          <Text fontWeight={block.blockLevel === 16 ? "semibold" : "normal"}>
            {formatBlockLevel(block.blockLevel)}
          </Text>
        </Td>
        <Td>{formatDuration(block.blockedAt, block.blockedUntil)}</Td>
        <Td>{dateFormatter.toDate(block.blockedAt)}</Td>
        <Td>{dateFormatter.toDate(block.blockedUntil)}</Td>
        <Td>{formatPercentage(block.cancellationRate)}</Td>
        <Td>{formatPercentage(block.lateCancellationRate)}</Td>
        <Td>{renderStatus()}</Td>
        <Td>
          {block.manualUnblockNote ? (
            <Text color="gray.600" fontSize="xs" maxW="200px" noOfLines={2}>
              {block.manualUnblockNote}
            </Text>
          ) : (
            <Text color="gray.400">-</Text>
          )}
        </Td>
        <Td>
          {block.isActive ? (
            <Button colorScheme="blue" size="xs" variant="outline" onClick={disclosure.onOpen}>
              Unblock
            </Button>
          ) : block.unblockedAt ? (
            <Tooltip
              hasArrow
              label={`Unblocked on ${dateFormatter.toDate(block.unblockedAt)} (${
                block.unblockedReason
              })`}
              placement="top"
            >
              <Text color="gray.500" cursor="help" fontSize="xs">
                -
              </Text>
            </Tooltip>
          ) : null}
        </Td>
      </Tr>

      <UnblockAlertDialog
        blockId={block.id}
        isOpen={disclosure.isOpen}
        onClose={disclosure.onClose}
        onSuccess={onUnblock}
      />
    </>
  );
}
